// Условия заполнения формы: form_schema.conditions.
// Формат и правила проверки — lk_backend/lk_app/task_data_validation.py (form_conditions)
// и @lk/form-renderer (formConditions.js).
import { v4 as uuidv4 } from 'uuid';

export const CONDITIONS_KEY = 'conditions';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const fieldNames = (names) =>
  Array.isArray(names) ? [...new Set(names.filter((name) => typeof name === 'string'))] : [];

// Приводит условия к рабочему виду; отсутствующие и битые части — пустые.
export function normalizeConditions(conditions) {
  const source = isPlainObject(conditions) ? conditions : {};
  const groups = Array.isArray(source.linked_groups) ? source.linked_groups : [];
  return {
    required_fields: fieldNames(source.required_fields),
    linked_groups: groups.filter(isPlainObject).map((group, index) => ({
      // id нужен только конструктору для редактирования группы, поэтому без него — по позиции.
      id: typeof group.id === 'string' && group.id ? group.id : `group-${index + 1}`,
      name: typeof group.name === 'string' ? group.name : '',
      fields: fieldNames(group.fields),
    })),
  };
}

export function getConditions(schema) {
  return normalizeConditions(schema?.[CONDITIONS_KEY]);
}

export function hasConditions(conditions) {
  return conditions.required_fields.length > 0 || conditions.linked_groups.length > 0;
}

// Убирает поля, которых больше нет в форме. Группы остаются, даже опустевшие.
export function pruneConditions(conditions, properties) {
  const normalized = normalizeConditions(conditions);
  const known = (name) => isPlainObject(properties) && name in properties;
  return {
    required_fields: normalized.required_fields.filter(known),
    linked_groups: normalized.linked_groups.map((group) => ({
      ...group,
      fields: group.fields.filter(known),
    })),
  };
}

// Заменяет ссылки на поля по mapping (Map); ссылки, которых нет в mapping, убираются.
export function mapConditionsFields(conditions, mapping) {
  const normalized = normalizeConditions(conditions);
  const mapFields = (names) =>
    fieldNames(names.filter((name) => mapping.has(name)).map((name) => mapping.get(name)));
  return {
    required_fields: mapFields(normalized.required_fields),
    linked_groups: normalized.linked_groups.map((group) => ({
      ...group,
      fields: mapFields(group.fields),
    })),
  };
}

// Соответствие между id и именем полей (Control) структуры редактора:
// from/to — 'id' или 'name'. При повторе ключа берётся первое поле.
export function controlFieldMap(structure, from, to) {
  const mapping = new Map();
  const visit = (element) => {
    if (!isPlainObject(element)) return;
    if (element.type === 'Control' && typeof element.scope === 'string') {
      const field = { id: element.id, name: element.scope.split('/').pop() };
      if (!mapping.has(field[from])) mapping.set(field[from], field[to]);
    }
    if (Array.isArray(element.elements)) element.elements.forEach(visit);
  };
  visit(structure);
  return mapping;
}

// Поля формы в порядке размещения в uiSchema, затем поля без Control.
export function listFormFields(schema, uiSchema) {
  const properties = isPlainObject(schema?.properties) ? schema.properties : {};
  const layoutGroups = new Map();
  const visit = (element, groupLabel) => {
    if (!isPlainObject(element)) return;
    const label = element.type === 'Group' ? element.label || '' : groupLabel;
    if (element.type === 'Control' && typeof element.scope === 'string') {
      const name = element.scope.split('/').pop();
      if (name in properties && !layoutGroups.has(name)) layoutGroups.set(name, label);
    }
    if (Array.isArray(element.elements)) element.elements.forEach((child) => visit(child, label));
  };
  visit(uiSchema, '');

  const names = [
    ...layoutGroups.keys(),
    ...Object.keys(properties).filter((name) => !layoutGroups.has(name)),
  ];
  return names.map((name) => {
    const fieldSchema = isPlainObject(properties[name]) ? properties[name] : {};
    return {
      name,
      title: typeof fieldSchema.title === 'string' && fieldSchema.title !== name ? fieldSchema.title : '',
      filled: fieldSchema.filled || 'lab',
      layoutGroup: layoutGroups.get(name) ?? null,
    };
  });
}

// Схема с новыми условиями; пустые условия из схемы удаляются.
export function withConditions(schema, conditions) {
  const newSchema = { ...schema };
  if (hasConditions(conditions)) {
    newSchema[CONDITIONS_KEY] = conditions;
  } else {
    delete newSchema[CONDITIONS_KEY];
  }
  return newSchema;
}

export function setFieldRequired(conditions, fieldName, required) {
  const others = conditions.required_fields.filter((name) => name !== fieldName);
  return {
    ...conditions,
    required_fields: required ? [...others, fieldName] : others,
  };
}

export function addLinkedGroup(conditions) {
  const name = `Группа ${conditions.linked_groups.length + 1}`;
  return {
    ...conditions,
    linked_groups: [...conditions.linked_groups, { id: uuidv4(), name, fields: [] }],
  };
}

function updateGroup(conditions, groupId, update) {
  return {
    ...conditions,
    linked_groups: conditions.linked_groups.map((group) =>
      group.id === groupId ? update(group) : group
    ),
  };
}

export function renameLinkedGroup(conditions, groupId, name) {
  return updateGroup(conditions, groupId, (group) => ({ ...group, name }));
}

export function removeLinkedGroup(conditions, groupId) {
  return {
    ...conditions,
    linked_groups: conditions.linked_groups.filter((group) => group.id !== groupId),
  };
}

export function addFieldToGroup(conditions, groupId, fieldName) {
  return updateGroup(conditions, groupId, (group) => ({
    ...group,
    fields: group.fields.includes(fieldName) ? group.fields : [...group.fields, fieldName],
  }));
}

export function removeFieldFromGroup(conditions, groupId, fieldName) {
  return updateGroup(conditions, groupId, (group) => ({
    ...group,
    fields: group.fields.filter((name) => name !== fieldName),
  }));
}
